import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "@/lib/api";
import { useUserContext } from "@/contexts/UserContext";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Users } from "lucide-react";

type Role = "player" | "coach" | "venue_owner";

const ROLE_HOME: Record<Role, string> = {
  venue_owner: "/venue/dashboard",
  coach:       "/coach/dashboard",
  player:      "/app/home",
};

/** Intenta obtener el token de sesión */
async function resolveToken(): Promise<string | null> {
  // Try immediately
  let token = await getAuthToken();
  if (token) return token;
  // Supabase may need a moment after signup — retry once
  await new Promise(r => setTimeout(r, 500));
  token = await getAuthToken();
  return token;
}

const CompleteProfile = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const { user, profile: ctxProfile, refreshProfile } = useUserContext();
  const navigate = useNavigate();
  const { toast } = useToast();

  /**
   * Obtener JWT de Supabase Auth — disponible instantáneamente, sin retry.
   */
  const getToken = async (): Promise<string | null> => {
    return resolveToken();
  };

  // Detectar si viene por invitación del coach
  const inviteToken = localStorage.getItem("invite_token");
  const isInvited = !!inviteToken;

  // Si ya tiene perfil con rol, redimir invitación si aplica y redirigir
  useEffect(() => {
    const check = async () => {
      // 1) Si ya tenemos perfil en el contexto (de checkAuthState), usarlo directamente
      //    sin hacer otra llamada al API
      if (ctxProfile?.role) {
        const token = await resolveToken();
        if (inviteToken) {
          if (ctxProfile.role === "coach" || ctxProfile.role === "admin") {
            navigate(`/join/${inviteToken}`, { replace: true });
            return;
          }
          // Es player con perfil existente — redimir directo
          if (token) {
            try {
              await fetch("/api/player/redeem-invite", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ inviteToken }),
              });
            } catch {
              console.warn("No se pudo redimir la invitación");
            }
          }
          localStorage.removeItem("invite_token");
          navigate("/app/classes", { replace: true });
          return;
        }
        navigate(ROLE_HOME[ctxProfile.role as Role] ?? "/app/home", { replace: true });
        return;
      }

      // 2) Sin perfil en contexto: intentar fetch desde la API (recarga de página)
      try {
        const token = await resolveToken();
        if (!token) { setChecking(false); return; }

        const res = await fetch("/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const profile = await res.json();
          if (profile?.role) {
            if (inviteToken) {
              if (profile.role === "coach" || profile.role === "admin") {
                navigate(`/join/${inviteToken}`, { replace: true });
                return;
              }
              try {
                await fetch("/api/player/redeem-invite", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ inviteToken }),
                });
              } catch {
                console.warn("No se pudo redimir la invitación");
              }
              localStorage.removeItem("invite_token");
              navigate("/app/classes", { replace: true });
              return;
            }
            navigate(ROLE_HOME[profile.role as Role] ?? "/app/home", { replace: true });
            return;
          }
        }
        // 404 o error = sin perfil, mostrar formulario (esperado para usuarios nuevos)
      } catch {
        // sin perfil aún — mostrar formulario
      }
      setChecking(false);
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, ctxProfile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Siempre player — coaches y centros se crean desde admin
    const finalRole: Role = "player";

    try {
      const token = await resolveToken();
      if (!token) {
        throw new Error("No hay sesión activa. Si te registraste con email, revisa tu bandeja para confirmar tu cuenta.");
      }
      const res = await fetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          displayName: localStorage.getItem("register_displayName") || user?.email?.split("@")[0] || "Usuario",
          role: finalRole,
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({})) as { error?: string; debug?: string };
        throw new Error(errBody.debug || errBody.error || "No se pudo guardar el perfil");
      }

      // Si hay invite token, intentar redimir la invitación (vincular alumno-coach)
      if (inviteToken) {
        try {
          await fetch("/api/player/redeem-invite", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ inviteToken }),
          });
        } catch {
          // No bloquear el flujo si falla la redención
          console.warn("No se pudo redimir la invitación");
        }
        localStorage.removeItem("invite_token");
      }

      // Actualizar el contexto con el nuevo profile
      await refreshProfile();
      localStorage.removeItem("register_displayName");
      toast({ title: isInvited ? "¡Te has unido como estudiante!" : "¡Perfil completado!" });
      navigate(ROLE_HOME[finalRole], { replace: true });
    } catch (err: unknown) {
      toast({ title: "Error", description: (err as Error).message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (checking) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-2">
          <CardTitle className="text-2xl font-bold">
            {isInvited ? "¡Tu coach te ha invitado!" : "¡Bienvenido a SportHub CL!"}
          </CardTitle>
          <CardDescription>
            {isInvited
              ? "Te unirás como estudiante. Presiona continuar para completar tu registro."
              : "Tu cuenta será configurada como jugador. Podrás reservar canchas y unirte a clases."}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent>
            <div className="flex items-start space-x-3 rounded-lg border border-green-500/30 bg-green-500/5 p-4">
              <Users className="h-6 w-6 text-green-500 mt-1 shrink-0" />
              <div>
                <p className="text-base font-medium">Jugador / Estudiante</p>
                <p className="text-sm text-muted-foreground">
                  {isInvited
                    ? "Serás vinculado con tu coach automáticamente."
                    : "Podrás reservar canchas, unirte a clases y seguir tu progreso."}
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Configurando..." : isInvited ? "Unirme como Estudiante" : "Continuar"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CompleteProfile;
